/**
 * station_panel.ts — the dock's STATION tab (space campaign K, 2026-10-01; report §8.3, §19.3, §19.6, §20.1, §22.2):
 * DOM over replicated rows; every action is a reducer; tokens only (THE TOKEN LAW); fits 720p at 200 %.
 *
 *   Role       what this station is for (its ONE difference), the interior cue, and the service row's status in the
 *              server's words — awaiting parts · unfunded · berth occupied (when the next cradle frees) · worker stale
 *   Repair     the quoted, material-backed job: a stored quote (the parts and their supplier, labour, fees, the role's
 *              cap, duration, expiry, the cancel rule) → Accept (a request id a retry reuses) → the cradle's progress →
 *              ONE receipt the flight pools take once (`bindRepairSink`) · Cancel (labour and fees back, bought parts
 *              yours — never both)
 *   Berth      a salvage yard's wreck berth: your lots in a wreck of this system, quoted and recovered
 *   Muster     a fort's board: the war cycle's campaign summary from the server's rows, each civ's operation, the named
 *              roster per side, and Muster (labour waived here for the cycle)
 *   Firms      the bounded planner's latest decision per miner — every candidate with its numbers and stop reason, the
 *              loss estimate with its interval, consigned bids in flight — and the treaty's one permission
 */
import { atlas } from '../state.js';
import { prebuiltSystems } from '@shared/galaxy.js';
import { stationsOf, STATION_ROLE_DEFS, stationRoleOf, type StationRole } from '@shared/space_stations.js';
import {
  ROLE_SERVICE, OUTAGE_LABEL, WORKER_STALE_S, TREATY_PERMISSION, PART_ORE_U, handInRoutes, treatyPermissionState, DEPOT_COOLDOWN_S,
} from '@shared/station_services.js';
import { decisionCycle } from '@shared/space_ops.js';
import { BASE_REFINE_EFF } from '@shared/station_services.js';
import {
  ensureStationRows, serviceRow, heartbeatAgeS, myQuote, myJobs, musterRoster, plans, consignments, treaties, firms, myFaction,
  myWrecksIn, myOreHere, subsystemsOf, stationServicesOnClient, msOf, safe, type JobRow, type QuoteRow,
} from './station_client.js';
import { keyHint } from './keybinds.js';
import type { DockHost } from './market_panel.js';

interface Id { isEqual(o: unknown): boolean; toHexString(): string }
interface Ctx { me: Id; sov: bigint; activeShip: { shipId: bigint; chassisKey: string; name: string } | null }
export interface FlightPools { hull: number; hpMax: number; missiles: number; missilesMax: number; counters: number; countersMax: number }

let poolsOf: (() => FlightPools | null) | null = null;
/** SpaceModule hands the panel its live flight pools (the hull's HP is the client's — gap 30 — the quote clamps it). */
export function bindFlightPools(fn: () => FlightPools | null): void { poolsOf = fn; }

const S = {
  head: 'color:var(--ax-hi);font:700 11px var(--ax-mono);letter-spacing:1px;margin-top:6px',
  sub: 'color:var(--ax-soft);font:11px var(--ax-mono);line-height:1.45',
  text: 'color:var(--ax-text);font:12px var(--ax-font);line-height:1.4',
  warn: 'color:var(--ax-warn);font:12px var(--ax-font);line-height:1.4',
  good: 'color:var(--ax-good);font:12px var(--ax-font);line-height:1.4',
  card: 'display:flex;flex-direction:column;gap:3px;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:8px 10px',
  label: 'color:var(--ax-muted);font:600 10px var(--ax-mono);letter-spacing:.6px',
  bar: 'height:6px;background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);overflow:hidden',
};
const sov = (v: bigint | number): string => `${BigInt(v).toLocaleString('en-US')} ⓒ`;
const stationList = (): Array<{ key: string; name: string; systemId: string; role: StationRole }> =>
  safe(() => prebuiltSystems().flatMap((s) => stationsOf(s).map((st) => ({ key: st.key, name: st.name, systemId: st.systemId, role: st.role }))), []);
export function stationName(key: string): string { return stationList().find((s) => s.key === key)?.name ?? key; }
function el<K extends keyof HTMLElementTagNameMap>(tag: K, css: string, text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.style.cssText = css;
  if (text) e.textContent = text;
  return e;
}
function line(parent: HTMLElement, text: string, css = S.text, data = ''): HTMLDivElement {
  const d = el('div', css, text);
  if (data) d.dataset.stn = data;
  parent.appendChild(d);
  return d;
}
const mins = (s: number): string => (s >= 90 ? `${Math.round(s / 60)} min` : `${Math.max(0, Math.round(s))} s`);
const reqIds = new Map<string, string>();
/** One request id per quote: a second click (or a retry after a lost reply) returns the same job, never a second one. */
function requestIdFor(quoteId: bigint): string {
  const k = quoteId.toString();
  let r = reqIds.get(k);
  if (!r) { r = `stn-${k}-${Math.floor(Math.random() * 1e9).toString(36)}`; reqIds.set(k, r); }
  return r;
}
let partsChoice: 'station' | 'own' = 'station';
interface Reducers {
  quoteStationRepair(a: { stationKey: string; shipId: bigint; missingHp: number; missiles: number; counters: number; source: string }): Promise<void>;
  quoteWreckRecovery(a: { stationKey: string; wreckId: bigint }): Promise<void>;
  acceptStationService(a: { quoteId: bigint; requestId: string }): Promise<void>;
  completeStationService(a: { jobId: bigint }): Promise<void>;
  cancelStationService(a: { jobId: bigint }): Promise<void>;
  musterAtFort(a: { stationKey: string }): Promise<void>;
}
const red = (): Reducers | null => (atlas.conn ? (atlas.conn.reducers as unknown as Reducers) : null);

// ── The tab ─────────────────────────────────────────────────────────────────────────────────────────────────────
export function renderStation(host: DockHost, body: HTMLElement, ctx: Ctx): void {
  ensureStationRows();
  const { stationKey, systemId } = host.stationCtx();
  const role = stationRoleOf(stationKey);
  const def = STATION_ROLE_DEFS[role];
  const on = stationServicesOnClient();
  const nowMs = Date.now();
  const wrap = el('div', 'display:flex;flex-direction:column;gap:6px', '');
  wrap.dataset.stationTab = stationKey;
  body.appendChild(wrap);

  // ── Role ──
  const roleCard = el('div', S.card);
  roleCard.dataset.stn = 'role';
  line(roleCard, `${stationName(stationKey).toUpperCase()} — ${def.label.toUpperCase()}`, S.head, 'role-head');
  line(roleCard, `Come here to ${def.reason}. ${def.difference}`, S.text, 'role-difference');
  line(roleCard, `Inside: ${def.cue}.`, S.sub);
  if (!on) line(roleCard, '⚠ Station services are switched off on this shard (station_services_v1): every station runs the shared base services, the 25 ⓒ repair is the old acknowledgement.', S.warn, 'off');
  wrap.appendChild(roleCard);
  if (!on) return;

  const row = serviceRow(stationKey);
  const svc = ROLE_SERVICE[role];
  const stale = heartbeatAgeS(nowMs) > WORKER_STALE_S;
  const status = el('div', S.card);
  status.dataset.stn = 'status';
  let numbers: { nextFreeAt?: number; budgetLeft?: number | null; reserveU?: number; firm?: string } = {};
  try { numbers = JSON.parse(row?.numbersJson ?? '{}'); } catch { /* fine */ }
  const busy = row?.busy ?? 0;
  const source = row ? (row.source === 'firm'
    ? `${row.partsAvail} part${row.partsAvail === 1 ? '' : 's'} from ${numbers.firm || 'the station firm'} (its ore above the ${numbers.reserveU ?? 0} u it keeps for itself)`
    : `${row.partsAvail}/${row.reliefCap} parts in the platform relief stock (restocked hourly; ${numbers.budgetLeft ?? '?'} left in the programme's day budget)`) : 'not reported yet';
  line(status, `Repair cradles ${busy}/${svc.berths} busy · parts: ${source}${svc.restoreCapPct < 100 ? ` · limited: the hull to ${svc.restoreCapPct} %` : ''}`, S.sub, 'service-line');
  const outage = stale ? 'worker_stale' : (row?.outage ?? '');
  if (outage) {
    const label = OUTAGE_LABEL[outage as keyof typeof OUTAGE_LABEL] ?? outage;
    const more = outage === 'berth_occupied' && numbers.nextFreeAt ? ` — the next cradle frees in ${mins(numbers.nextFreeAt - nowMs / 1000)}`
      : outage === 'worker_stale' ? ` — the station's crew last reported ${mins(heartbeatAgeS(nowMs))} ago (a job still completes on the server's clock)`
      : outage === 'awaiting_parts' ? ' — bring your own ferrite ore, or wait for stock' : outage === 'unfunded' ? ' — the relief programme\'s budget for today is spent' : '';
    line(status, `⚠ ${label.toUpperCase()}${more}`, S.warn, 'outage');
  } else line(status, '✔ open — the cradles take work now', S.good, 'outage');
  wrap.appendChild(status);

  renderRepair(host, wrap, ctx, stationKey, role, nowMs);
  if (role === 'salvage_yard') renderBerth(host, wrap, stationKey, systemId, nowMs);
  if (role === 'military_fort') renderMuster(host, wrap, stationKey, nowMs);
  if (role === 'refinery') {
    const r = el('div', S.card);
    line(r, `THE REFINE LINE — ${Math.round(svc.refineEff * 100)} % here (${Math.round(BASE_REFINE_EFF * 100)} % at other stations; your own refinery beacon runs 50 %)`, S.head, 'refine');
    r.appendChild(host.button('⚙ Refine at the Industry tab', true, () => host.goTab('industry')));
    wrap.appendChild(r);
  }
  if (role === 'trade_hub') {
    const r = el('div', S.card);
    line(r, `THE EXCHANGE HERE IS ${svc.depthScale}× DEEP — a bulk lot moves the 2 h mark ${svc.depthScale}× less far than at another station`, S.head, 'depth');
    r.appendChild(host.button('⇄ Open the Exchange', true, () => host.goTab('exchange')));
    wrap.appendChild(r);
  }
  if (role === 'research_post') {
    const r = el('div', S.card);
    line(r, `THE SURVEY DESK — funded recovery tenders and the hazard census (${keyHint('spaceSurvey')} opens the desk)`, S.head, 'desk');
    wrap.appendChild(r);
  }
  renderFirms(wrap, nowMs);
}

// ── Repair ──────────────────────────────────────────────────────────────────────────────────────────────────────
function renderRepair(host: DockHost, wrap: HTMLElement, ctx: Ctx, stationKey: string, role: StationRole, nowMs: number): void {
  const card = el('div', S.card);
  card.dataset.stn = 'repair';
  line(card, 'REPAIR & REARM — a quoted job (station service v2)', S.head);
  const pools = poolsOf?.() ?? null;
  const ship = ctx.activeShip;
  if (!ship) { line(card, 'No active hull in the cradle.', S.sub); wrap.appendChild(card); return; }
  const missing = pools ? Math.max(0, Math.round(pools.hpMax - pools.hull)) : 0;
  const mMiss = pools ? Math.max(0, pools.missilesMax - pools.missiles) : 0;
  const cMiss = pools ? Math.max(0, pools.countersMax - pools.counters) : 0;
  const g = subsystemsOf(ship.shipId);
  line(card, `${ship.name}: hull ${pools ? `${Math.round(pools.hull)}/${pools.hpMax}` : '?'} · missiles ${pools ? `${pools.missiles}/${pools.missilesMax}` : '?'} · counters ${pools ? `${pools.counters}/${pools.countersMax}` : '?'}${g ? ` · engine ${g.engine} · weapons ${g.weapons} (the groups come back free at a dock)` : ''}`, S.sub, 'pools');
  const own = myOreHere(stationKey, ship.shipId);
  const pick = el('div', 'display:flex;gap:6px;flex-wrap:wrap;align-items:center');
  pick.appendChild(el('span', S.label, 'PARTS'));
  pick.appendChild(host.button(`${partsChoice === 'station' ? '● ' : ''}from the station`, true, () => { partsChoice = 'station'; }));
  pick.appendChild(host.button(`${partsChoice === 'own' ? '● ' : ''}my own ore (${own} u here)`, own >= PART_ORE_U, () => { partsChoice = 'own'; }));
  card.appendChild(pick);
  const working = myJobs().find((j) => j.state === 'working' && j.kind === 'repair' && j.shipId === ship.shipId);
  const q = myQuote(stationKey, nowMs);
  card.appendChild(host.button(q && q.kind === 'repair' ? '↻ Re-quote' : '⚙ Get a quote', !working && (missing > 0 || mMiss + cMiss > 0), () =>
    red()!.quoteStationRepair({ stationKey, shipId: ship.shipId, missingHp: missing, missiles: mMiss, counters: cMiss, source: partsChoice })));
  if (!working && missing <= 0 && mMiss + cMiss <= 0) line(card, 'The hull is sound and the racks are full.', S.sub);
  if (q && q.kind === 'repair' && q.shipId === ship.shipId) quoteCard(host, card, q, nowMs, role);
  wrap.appendChild(card);
  jobsCard(host, wrap, stationKey, nowMs);
}

function quoteCard(host: DockHost, parent: HTMLElement, q: QuoteRow, nowMs: number, role: StationRole): void {
  const c = el('div', 'display:flex;flex-direction:column;gap:2px;border-top:1px dashed var(--ax-line2);padding-top:5px');
  c.dataset.stn = 'quote';
  const left = Math.max(0, Math.round((msOf(q.expiresAt) - nowMs) / 1000));
  if (q.kind === 'repair') {
    line(c, `QUOTE #${q.quoteId} — binds this hull's fit (rev ${q.fitRev}), the station, service v${q.version}; holds ${mins(left)}`, S.label);
    line(c, `+${q.restoreHp} hull${q.missingHp > q.restoreHp ? ` (${q.missingHp} missing — this ${STATION_ROLE_DEFS[role].label.toLowerCase()} patches to its cap)` : ''}${q.missiles + q.counters > 0 ? ` · rearm ${q.missiles} missile${q.missiles === 1 ? '' : 's'} + ${q.counters} counter${q.counters === 1 ? '' : 's'}` : ''}`, S.text, 'q-restore');
    const src = q.source === 'own' ? 'your own ore (no charge)' : q.source === 'firm' ? `bought from the station firm at ${q.partUnitSov} ⓒ each` : `from the relief stock at ${q.partUnitSov} ⓒ each`;
    line(c, `parts ${q.parts} × ${PART_ORE_U} u ferrite ore — ${src}: ${sov(q.partsSov)}`, S.sub, 'q-parts');
    line(c, `labour ${sov(q.labourSov)}${q.mustered && role === 'military_fort' ? ' (waived — you mustered)' : ''} · rearm ${sov(q.rearmSov)} · operator fee ${sov(q.operatorFeeSov)} · platform fee ${sov(q.platformFeeSov)}`, S.sub, 'q-fees');
    line(c, `TOTAL ${sov(q.totalSov)} — escrowed when you accept · ${q.durationS} s in the cradle`, S.head, 'q-total');
    line(c, `Cancel before the work is done: ${sov(q.totalSov - q.partsSov)} back (labour, fees, munitions); the parts you bought are yours — ${q.parts * PART_ORE_U} u in your warehouse here. Never both.`, S.sub, 'q-cancel');
  } else {
    line(c, `RECOVERY QUOTE #${q.quoteId} — holds ${mins(left)}`, S.label);
    line(c, `fee ${sov(q.totalSov)} · the tug takes ${q.durationS} s`, S.head, 'q-total');
  }
  if (q.note) line(c, q.note, q.note.startsWith('⚠') ? S.warn : S.sub, 'q-note');
  c.appendChild(host.button(`✔ Accept — ${sov(q.totalSov)}`, left > 0, () => red()!.acceptStationService({ quoteId: q.quoteId, requestId: requestIdFor(q.quoteId) })));
  parent.appendChild(c);
}

function jobsCard(host: DockHost, wrap: HTMLElement, stationKey: string, nowMs: number): void {
  const jobs = myJobs().filter((j) => j.stationKey === stationKey).slice(0, 5);
  if (!jobs.length) return;
  const card = el('div', S.card);
  card.dataset.stn = 'jobs';
  line(card, 'YOUR JOBS HERE', S.head);
  for (const j of jobs) jobLine(host, card, j, nowMs);
  wrap.appendChild(card);
}
function jobLine(host: DockHost, parent: HTMLElement, j: JobRow, nowMs: number): void {
  const r = el('div', 'display:flex;flex-direction:column;gap:2px;border-top:1px solid var(--ax-line);padding-top:4px');
  r.dataset.stnJob = j.state;
  const kind = j.kind === 'repair' ? 'Repair' : 'Recovery';
  if (j.state === 'working') {
    const total = Math.max(1, (msOf(j.readyAt) - msOf(j.acceptedAt)) / 1000);
    const left = Math.max(0, (msOf(j.readyAt) - nowMs) / 1000);
    line(r, `${kind} #${j.jobId} — WORKING · ${left > 0 ? `${mins(left)} left` : 'ready — the crew is finishing'} · ${j.note}`, S.text);
    const bar = el('div', S.bar);
    bar.appendChild(el('div', `height:100%;width:${Math.round(100 * (1 - left / total))}%;background:var(--ax-accent)`));
    r.appendChild(bar);
    const btns = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    if (left <= 0) btns.appendChild(host.button('✔ Complete now', true, () => red()!.completeStationService({ jobId: j.jobId })));
    if (j.kind === 'repair') btns.appendChild(host.button('✖ Cancel', true, () => red()!.cancelStationService({ jobId: j.jobId })));
    r.appendChild(btns);
  } else {
    line(r, `${kind} #${j.jobId} — ${j.state.toUpperCase()} · ${j.note}`, j.state === 'completed' ? S.good : S.sub);
    let rc: Record<string, unknown> = {};
    try { rc = JSON.parse(j.receiptJson || '{}'); } catch { /* fine */ }
    line(r, `receipt: escrow tx ${rc.escrowTx ?? '—'}${rc.buyTx && rc.buyTx !== '0' ? ` · parts tx ${rc.buyTx}` : ''}${rc.completeTx && rc.completeTx !== '0' ? ` · labour tx ${rc.completeTx}` : ''}${rc.refundTx ? ` · refund tx ${rc.refundTx}` : ''} · request ${j.requestId}`, S.sub);
  }
  parent.appendChild(r);
}

// ── The wreck berth ─────────────────────────────────────────────────────────────────────────────────────────────
function renderBerth(host: DockHost, wrap: HTMLElement, stationKey: string, systemId: string, nowMs: number): void {
  const card = el('div', S.card);
  card.dataset.stn = 'berth';
  line(card, 'THE WRECK BERTH — the yard\'s tug brings in what YOU own from a wreck in this system', S.head);
  line(card, 'Rights: only lots you own (the port\'s crates and other owners\' goods stay where they lie), once, before strippers take them. A recovery cannot be cancelled once the tug is under way.', S.sub);
  const ws = myWrecksIn(systemId, nowMs);
  if (!ws.length) line(card, 'No wreck in this system holds anything of yours.', S.sub, 'berth-none');
  for (const w of ws) {
    const r = host.row();
    r.dataset.stnWreck = w.wreckId.toString();
    r.appendChild(el('div', 'flex:1;' + S.text, `${w.victimName}'s wreck — ${w.units} u of yours in ${w.lots} lot${w.lots === 1 ? '' : 's'} · fades in ${mins((w.expiresMs - nowMs) / 1000)}`));
    r.appendChild(host.button('⚙ Quote recovery', true, () => red()!.quoteWreckRecovery({ stationKey, wreckId: w.wreckId })));
    card.appendChild(r);
  }
  const q = myQuote(stationKey, nowMs);
  if (q && q.kind === 'recovery') quoteCard(host, card, q, nowMs, 'salvage_yard');
  wrap.appendChild(card);
  if (!myJobs().some((j) => j.stationKey === stationKey)) return;
}

// ── The muster board ────────────────────────────────────────────────────────────────────────────────────────────
interface CampaignRowL { campaignId: bigint; cyc: bigint; fortKey: string; targetSystem: string; state: string; winner: string }
interface SideRowL { campaignId: bigint; side: number; civ: string; capsCommitted: number; capsAlive: number; skirmCommitted: number; skirmAlive: number }
function renderMuster(host: DockHost, wrap: HTMLElement, stationKey: string, nowMs: number): void {
  const card = el('div', S.card);
  card.dataset.stn = 'muster';
  const cyc = decisionCycle(Math.floor(nowMs / 1000));
  line(card, `THE MUSTER BOARD — war cycle ${cyc}`, S.head);
  const db = atlas.conn?.db as unknown as { spaceCampaign?: { iter(): Iterable<CampaignRowL> }; campaignSide?: { iter(): Iterable<SideRowL> }; civOperation?: { iter(): Iterable<{ civ: string; cyc: bigint; operation: string; reason: string }> }; account?: { iter(): Iterable<{ identity: Id; username: string }> } } | undefined;
  const camp = safe(() => [...(db?.spaceCampaign?.iter() ?? [])].find((c) => Number(c.cyc) === cyc) ?? null, null);
  if (camp) {
    const sides = safe(() => [...(db?.campaignSide?.iter() ?? [])].filter((s) => s.campaignId === camp.campaignId), []);
    line(card, `Campaign #${camp.campaignId} (the server's row): ${camp.state}${camp.winner ? ` — ${camp.winner}` : ''} · the front ${camp.targetSystem || '—'}`, S.text, 'campaign');
    for (const s of sides) line(card, `${s.civ}: capitals ${s.capsAlive}/${s.capsCommitted} · gunships ${s.skirmAlive}/${s.skirmCommitted}`, S.sub);
  } else line(card, 'No campaign row for this cycle (a ceasefire, or the war opens later in the cycle).', S.sub, 'campaign');
  for (const o of safe(() => [...(db?.civOperation?.iter() ?? [])].filter((x) => Number(x.cyc) === cyc), [])) line(card, `${o.civ} council: ${o.operation} — ${o.reason}`, S.sub);
  const roster = musterRoster(BigInt(cyc));
  const nameOf = (id: Id): string => safe(() => [...(db?.account?.iter() ?? [])].find((a) => a.identity.isEqual(id))?.username ?? id.toHexString().slice(0, 8), id.toHexString().slice(0, 8));
  for (const civ of ['aurelian', 'borean']) {
    const names = roster.filter((r) => r.civ === civ).map((r) => `${nameOf(r.identity)} (${r.hull})`);
    line(card, `${civ.toUpperCase()} ROSTER (${names.length}): ${names.length ? names.join(', ') : 'nobody yet'}`, S.sub, `roster-${civ}`);
  }
  const mine = roster.find((r) => atlas.conn?.identity && r.identity.isEqual(atlas.conn.identity));
  const sworn = myFaction();
  card.appendChild(host.button(mine ? '✔ On this war\'s roster — labour waived here' : sworn ? `▶ Muster for the ${sworn} line` : '▶ Muster (swear to a civ first — the Duty tab)', !mine && !!sworn, () => red()!.musterAtFort({ stationKey })));
  wrap.appendChild(card);
}

// ── The firms' plans + the treaty's permission ──────────────────────────────────────────────────────────────────
interface PlanRowJ { id: string; kind: string; label: string; toKey: string; qty: number; stop: string; score: { netSov: number; perMin: number; saleSov: number; feesSov: number; escortSov: number; delaySov: number } | null }
function renderFirms(wrap: HTMLElement, nowMs: number): void {
  const card = el('div', S.card);
  card.dataset.stn = 'firms';
  line(card, 'THE FIRMS\' PLANS — the miners decide each hour from ≤ 8 candidates (the deterministic winner runs)', S.head);
  const fs = firms();
  for (const p of plans()) {
    const f = fs.find((x) => x.firmId === p.firmId);
    let j: { rows?: PlanRowJ[]; weak?: boolean; n?: number; permission?: string; stock?: number; ownYardSupplied?: boolean } = {};
    try { j = JSON.parse(p.rowsJson || '{}'); } catch { /* fine */ }
    const ago = mins((nowMs - msOf(p.decidedAt)) / 1000);
    const chosen = (j.rows ?? []).find((r) => r.id === p.chosen);
    line(card, `${f?.name ?? `firm ${p.firmId}`} (${stationName(f?.stationKey ?? '')}) — ${ago} ago: ${p.chosen === 'hold' ? 'HOLD' : `→ ${chosen?.label ?? p.chosen} · ${p.qty} u · expected net ${sov(p.netSov)} (${(Number(p.perMinX100) / 100).toFixed(1)} ⓒ/ship-min)`}`, p.chosen === 'hold' ? S.warn : S.text, `plan-${p.firmId}`);
    line(card, `loss estimate ${(p.pLossX1000 / 10).toFixed(1)} % [${(p.pLossLoX1000 / 10).toFixed(1)}–${(p.pLossHiX1000 / 10).toFixed(1)} %] from ${p.lost} lost / ${p.arrived} arrived in 24 h${j.weak ? ' — weak evidence' : ''} · ore at the station ${j.stock ?? '?'} u · own yard ${j.ownYardSupplied ? 'supplied' : 'short'}`, S.sub);
    for (const r of (j.rows ?? []).filter((x) => x.kind !== 'hold')) {
      line(card, `   ${r.id === p.chosen ? '▶' : '·'} ${r.label}: ${r.stop ? `✖ ${r.stop}` : `net ${sov(r.score?.netSov ?? 0)} = sale ${r.score?.saleSov ?? 0} − tax ${r.score?.feesSov ?? 0} − escort ${r.score?.escortSov ?? 0} − delay ${r.score?.delaySov ?? 0} (${(r.score?.perMin ?? 0).toFixed(1)}/min)`}`, S.sub);
    }
    if (p.chosen === 'hold' && p.stopReason) line(card, `why no run: ${p.stopReason}`, S.sub);
  }
  if (!plans().length) line(card, 'No plan yet — the miners decide at their next hourly step.', S.sub, 'plans-none');
  for (const c of consignments().filter((x) => x.state === 'riding')) line(card, `consigned: ${c.qty} u to bid #${c.orderId} at ${stationName(c.stationKey)} (${c.priceSov} ⓒ/u held in escrow) — filled on arrival; a lost run gives the units back to the bid (or the escrow back, if the bid left the book)`, S.text, 'consign');
  // The treaty's one permission (report §20.1 last ¶): what it changes, for how long, which routes.
  const cyc = decisionCycle(Math.floor(nowMs / 1000));
  const t = treaties().filter((x) => x.kind === TREATY_PERMISSION.kind && (x.state === 'standing' || x.state === 'proposed'))
    .map((x) => ({ state: x.state, fromCyc: Number(x.fromCyc), untilCyc: Number(x.untilCyc) }))
    .sort((a, b) => (a.state === 'standing' ? -1 : 1) - (b.state === 'standing' ? -1 : 1))[0] ?? null;
  const st = treatyPermissionState(t, cyc);
  line(card, `TREATY — ${TREATY_PERMISSION.label}: ${st.line}`, st.open ? S.good : S.head, 'treaty');
  line(card, `${TREATY_PERMISSION.text} Routes: ${handInRoutes().map((r) => `${r.miner} → ${r.yard} (${stationName(r.fromKey)} → ${stationName(r.toKey)})`).join(' · ')}`, S.sub, 'treaty-routes');
  wrap.appendChild(card);
}
/** The depot's cap, for the HUD toast. */
export const DEPOT_COOLDOWN_MIN = DEPOT_COOLDOWN_S / 60;
